/**
 * @fileoverview Browser logging package for puffin-core
 * @module @puffin-core/logging-browser
 */

// Core Logger
export {
  Logger,
  createLogger,
  resetLoggerInstance,
  RESERVED_CONTEXT_KEYS,
  ReservedContextKeyError,
} from './Logger.js';
export { createAppLogger } from './createAppLogger.js';
export { createErrorSink } from './createErrorSink.js';
export { installGlobalErrorCapture, GLOBAL_ERROR_KIND } from './captureGlobalErrors.js';
export { createReactRootErrorOptions } from './reactRootErrorOptions.js';
export {
  installWebVitalsCollector,
  supportsWebVitals,
  toVitalRecord,
  WEB_VITAL_REGISTRARS,
} from './collectWebVitals.js';
export {
  TELEMETRY_CONSENT,
  setTelemetryConsent,
  getTelemetryConsent,
  onTelemetryConsentChange,
  resetTelemetryConsent,
} from './telemetryConsent.js';

// Constants
export { LOG_LEVEL, LOG_LEVEL_PRIORITY, SENSITIVE_FIELDS } from './constants.js';

// Utilities
export { sanitizeData } from './utils/sanitize.js';
export { isBrowser, isDevelopment, isProduction, getLogLevelFromEnv } from './utils/environment.js';

// Transports
export {
  ServerTransport,
  createServerTransport,
  CLIENT_LOG_INGEST_PATH,
  SHIP_INFO,
} from './transports/server.js';
export { createFetchLogClient } from './transports/fetchLogClient.js';
export { ConsoleTransport, createConsoleTransport } from './transports/console.js';

// Convenience logger wrapper
import { createLogger } from './Logger.js';
import { LOG_LEVEL } from './constants.js';

/**
 * Convenience logger instance with all methods pre-bound
 * This allows direct use like: logger.info('message')
 */
const loggerMethods = {
  get trace() {
    return createLogger().trace.bind(createLogger());
  },
  get debug() {
    return createLogger().debug.bind(createLogger());
  },
  get info() {
    return createLogger().info.bind(createLogger());
  },
  get warn() {
    return createLogger().warn.bind(createLogger());
  },
  get error() {
    return createLogger().error.bind(createLogger());
  },
  get critical() {
    return createLogger().critical.bind(createLogger());
  },
  get logError() {
    return createLogger().logError.bind(createLogger());
  },
  get timing() {
    return createLogger().timing.bind(createLogger());
  },
  get configure() {
    return createLogger().configure.bind(createLogger());
  },
  get setContext() {
    return createLogger().setContext.bind(createLogger());
  },
  LOG_LEVEL,
};

export const logger = loggerMethods;

// Default export
import { Logger as LoggerClass } from './Logger.js';
import { sanitizeData as sanitizeDataFn } from './utils/sanitize.js';

export default {
  Logger: LoggerClass,
  createLogger,
  LOG_LEVEL,
  sanitizeData: sanitizeDataFn,
};
